import type {
  CompanyDossier,
  InvestorDashboard,
  ManagerScope,
  Page,
  PublishedUpdate,
  StaffDashboard,
  UpdateKind,
} from '@fruuit/shared'
import { keepPreviousData } from '@tanstack/react-query'
import { useApiQuery } from '../../api/hooks'

export function useInvestorDashboard(org: string | null = null) {
  return useApiQuery<InvestorDashboard>(
    ['dashboard', 'investor', org ?? 'own'],
    '/dashboard/investor',
    {
      query: { org },
    },
  )
}

export function useStaffDashboard(managers: ManagerScope, options: { enabled?: boolean } = {}) {
  return useApiQuery<StaffDashboard>(['dashboard', 'staff', managers], '/dashboard/staff', {
    query: { managers },
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  })
}

export function useUpdates(options: {
  cursor: string | null
  kind?: UpdateKind | undefined
  org?: string | null
}) {
  const { cursor, kind, org = null } = options
  return useApiQuery<Page<PublishedUpdate>>(
    ['updates', org ?? 'own', kind ?? 'all', cursor],
    '/updates',
    {
      query: { cursor, kind, org, limit: 20 },
      placeholderData: keepPreviousData,
    },
  )
}

export function useDossier(issuerId: string, org: string | null) {
  return useApiQuery<CompanyDossier>(
    ['company', issuerId, org ?? 'own'],
    `/companies/${encodeURIComponent(issuerId)}`,
    {
      query: { org },
    },
  )
}

/** Kind-specific link text (GAP-UI-003). */
export const UPDATE_LINK_LABEL: Record<UpdateKind, string> = {
  assessments: 'Read the assessment',
  milestones: 'Read the evidence review',
  dialogues: 'Read the correspondence',
}

export const UPDATE_KIND_LABEL: Record<UpdateKind, string> = {
  assessments: 'Assessment',
  milestones: 'Evidence review',
  dialogues: 'Correspondence',
}
